import React, { useState } from 'react';
import { Button, ColorPicker, Flex, InputNumber, Popover, Select, Tooltip } from 'antd';
import { PlusOutlined, UndoOutlined, WarningOutlined } from '@ant-design/icons';
import DesignTokenPicker, { TokenPreview } from 'admin/components/common/DesignTokenPicker';
import { useTheme } from 'admin/components/common/Theme';
import {
	DESIGN_PRESET_ENUM_VALUES,
	getCompatibleDesignTokens,
	resolveDesignModeValue,
	getDesignPresetTokenId,
	getDesignPresetInheritedAssignment,
	normalizeDesignPresetValue,
	DESIGN_TOKEN_PROPERTY_CONFIG,
	type DesignDefinition,
	type DesignPreset,
	type DesignPresetProperty,
	type DesignPresetValue,
} from 'common/design-tokens';
import t from 'utils/translate';
import type { StoryDefaultPlatform, StoryMediaQuery } from 'types/story';
import {
	buildFontFamilySelectOptions,
	isFontFamilyAvailable,
	type FontFamilySelectOption,
	type FontFamilySelectOptionGroup,
} from 'admin/components/common/Form/font-family-options';
import FontFamilyUnavailableOption from 'admin/components/common/Form/FontFamilyUnavailableOption';
import { getDesignPresetDisplayValue, getDesignPresetPlatformName } from './design-preset-display';
import ShadowControl from './ShadowControl';
import css from './DesignPresetsEditor.scss';

export const defaultLiteral = (property: DesignPresetProperty, design: DesignDefinition): DesignPresetValue => {
	if (property === 'fontFamily') return design.defaults.root.fontFamily.base;
	if (property === 'fontWeight') return 400;
	if (property === 'lineHeight') return 1.5;
	if (property === 'color' || property === 'backgroundColor' || property === 'borderColor') return 'rgba(0, 0, 0, 1)';
	if (property === 'opacity') return 1;
	if (property === 'boxShadow' || property === 'textShadow') return 'none';
	if (property in DESIGN_PRESET_ENUM_VALUES) {
		const values: readonly string[] = DESIGN_PRESET_ENUM_VALUES[property as keyof typeof DESIGN_PRESET_ENUM_VALUES];
		return values.includes('none') ? 'none' : values[0];
	}
	return property === 'fontSize' ? '18px' : '0px';
};
function LiteralControl({
	property,
	value,
	fonts,
	disabled,
	onChange,
}: {
	property: DesignPresetProperty;
	value: DesignPresetValue;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	onChange: (value: DesignPresetValue) => void;
}) {
	if (property === 'fontFamily') {
		const family = String(value);
		const select = buildFontFamilySelectOptions(
			family,
			fonts.map(font => ({ value: font.fontFamily, label: font.fontFamily }))
		);
		return (
			<Select<string, FontFamilySelectOption | FontFamilySelectOptionGroup>
				disabled={disabled}
				className={css.select}
				popupMatchSelectWidth={false}
				aria-label={property}
				showSearch
				optionFilterProp="label"
				value={select.value}
				options={select.options}
				labelRender={({ label }) =>
					select.unavailable ? <FontFamilyUnavailableOption family={family} compact /> : label
				}
				optionRender={option =>
					'unavailable' in option.data && option.data.unavailable ? (
						<FontFamilyUnavailableOption family={String(option.data.value)} />
					) : (
						option.label
					)
				}
				onChange={onChange}
			/>
		);
	}
	if (property === 'color' || property === 'backgroundColor' || property === 'borderColor')
		return (
			<ColorPicker
				disabled={disabled}
				value={String(value)}
				showText
				onChange={color => onChange(color.toRgbString())}
			/>
		);
	if (property in DESIGN_PRESET_ENUM_VALUES)
		return (
			<Select
				disabled={disabled}
				className={css.select}
				aria-label={property}
				value={String(value)}
				options={DESIGN_PRESET_ENUM_VALUES[property as keyof typeof DESIGN_PRESET_ENUM_VALUES].map(item => ({
					value: item,
					label:
						property === 'borderStyle'
							? t(`presets.borderStyles.${item}`)
							: property === 'direction'
								? t(`presets.directions.${item}`)
								: item,
				}))}
				onChange={onChange}
			/>
		);
	if (property === 'boxShadow' || property === 'textShadow')
		return <ShadowControl property={property} value={String(value)} disabled={disabled} onChange={onChange} />;
	const number = typeof value === 'number' ? value : Number.parseFloat(value);
	const unitless =
		property === 'fontWeight' || property === 'opacity' || (property === 'lineHeight' && typeof value === 'number');
	return (
		<Flex wrap gap="small" className={css.numberControl}>
			<InputNumber
				disabled={disabled}
				suffix={unitless ? undefined : 'px'}
				min={property === 'opacity' ? 0 : undefined}
				max={property === 'opacity' ? 1 : undefined}
				step={property === 'opacity' || (property === 'lineHeight' && unitless) ? 0.1 : 1}
				aria-label={property}
				value={Number.isFinite(number) ? number : null}
				onChange={next => {
					if (next !== null) onChange(unitless ? next : `${next}px`);
				}}
			/>
			{property === 'lineHeight' ? (
				<Select
					disabled={disabled}
					className={css.unit}
					aria-label={t('presets.unit')}
					value={typeof value === 'number' ? 'number' : 'px'}
					options={[
						{ value: 'number', label: t('presets.unitless') },
						{ value: 'px', label: 'px' },
					]}
					onChange={unit => onChange(unit === 'number' ? number || 0 : `${number || 0}px`)}
				/>
			) : null}
		</Flex>
	);
}
function LiteralEditor({
	property,
	value,
	fonts,
	disabled,
	onSave,
	onClose,
}: {
	property: DesignPresetProperty;
	value: DesignPresetValue;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	onSave: (value: DesignPresetValue) => void;
	onClose: () => void;
}) {
	const [draft, setDraft] = useState(value);
	const { theme } = useTheme();
	let normalized: DesignPresetValue | undefined;
	let error: string | undefined;
	try {
		normalized = normalizeDesignPresetValue(property, draft);
	} catch (issue) {
		error = issue instanceof Error ? issue.message : String(issue);
	}
	return (
		<div
			className={css.literalEditor}
			data-theme={theme}
			onKeyDown={event => {
				if (event.key === 'Escape') {
					event.stopPropagation();
					onClose();
				}
			}}
		>
			<LiteralControl property={property} value={draft} fonts={fonts} disabled={disabled} onChange={setDraft} />
			{error ? (
				<div className={css.fieldError} role="alert">
					{error}
				</div>
			) : null}
			<Flex gap={8} justify="flex-end" className={css.literalActions}>
				<Button onClick={onClose}>{t('brandKit.cancel')}</Button>
				<Button
					type="primary"
					disabled={disabled || normalized === undefined}
					onClick={() => {
						if (normalized !== undefined) onSave(normalized);
					}}
				>
					{t('presets.assign')}
				</Button>
			</Flex>
		</div>
	);
}

export default function AssignmentCell({
	property,
	display,
	assigned,
	inherited,
	design,
	fonts,
	modeSelection,
	disabled,
	isBase,
	assignmentLabel,
	mixed = false,
	onChange,
}: {
	property: DesignPresetProperty;
	display: ReturnType<typeof getDesignPresetDisplayValue>;
	assigned: DesignPresetValue | undefined;
	inherited?: { value: DesignPresetValue; source: string };
	design: DesignDefinition;
	fonts: { fontFamily: string }[];
	modeSelection: Record<string, string>;
	disabled?: boolean;
	isBase: boolean;
	assignmentLabel?: string;
	mixed?: boolean;
	onChange: (value: DesignPresetValue | undefined) => void;
}) {
	const [open, setOpen] = useState(false);
	const { theme } = useTheme();
	const [editing, setEditing] = useState(false);
	const [search, setSearch] = useState('');
	const compatible = getCompatibleDesignTokens(design.tokens, property);
	const effective = mixed ? undefined : assigned ?? inherited?.value;
	const tokenId = getDesignPresetTokenId(effective);
	const token = tokenId ? compatible.find(item => item.id === tokenId) : undefined;
	const tokenValue = token ? resolveDesignModeValue<string | number>(token.value, modeSelection) : undefined;
	const inheritedHint =
		!mixed && assigned === undefined && inherited ? `${t('presets.inherited')} ${inherited.source}` : undefined;
	const exactValue = display?.value ?? tokenValue ?? effective;
	const fontFamily = token ? tokenValue : tokenId ? undefined : exactValue;
	const unavailableFont =
		property === 'fontFamily' &&
		typeof fontFamily === 'string' &&
		!isFontFamilyAvailable(
			fontFamily,
			fonts.map(font => ({ value: font.fontFamily, label: font.fontFamily }))
		);
	const unresolved = (!!tokenId && !token) || unavailableFont;
	const displayedValue =
		typeof exactValue === 'string' && /^[-+]?(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(exactValue)
			? `${Number(parseFloat(exactValue).toFixed(1))}px`
			: exactValue;
	const valueLabel = token ? `${displayedValue} · ${token.name}` : tokenId ?? String(displayedValue ?? '—');
	const label = mixed ? 'Mixed' : valueLabel;
	const isColor = property === 'color' || property === 'backgroundColor' || property === 'borderColor';
	const supportsTokens = property in DESIGN_TOKEN_PROPERTY_CONFIG;
	const openPicker = () => {
		setSearch('');
		setEditing(false);
		setOpen(true);
	};
	const hint = mixed ? (
		'Different values. Assign to apply to all sides.'
	) : (
		<>
			{token ? <TokenPreview token={token} value={exactValue} /> : tokenId ?? String(exactValue ?? '—')}
			{display && display.cssValue !== display.authoredValue ? (
				<div>{`${display.authoredValue} → ${display.cssValue} → ${display.value}`}</div>
			) : null}
			{inheritedHint ? <div>{inheritedHint}</div> : null}
			{tokenId && !token ? <div>{t('presets.unresolved')}</div> : null}
			{unavailableFont ? <div>{t('presets.unavailable')}</div> : null}
		</>
	);
	return (
		<div className={css.assignment} data-inherited={assigned === undefined} data-open={open || editing}>
			<Popover
				trigger="click"
				placement="bottomLeft"
				open={(open || editing) && !disabled}
				onOpenChange={next => {
					if (!next) {
						setOpen(false);
						setEditing(false);
					}
				}}
				styles={{ container: { padding: 0 } }}
				content={
					open ? (
						<DesignTokenPicker
							dark={theme === 'dark'}
							search={search}
							onSearchChange={setSearch}
							onClose={() => setOpen(false)}
							modeSelection={modeSelection}
							sources={[
								{
									id: 'design',
									tokens: compatible,
									selectedTokenId: tokenId,
									onSelect: reference => {
										onChange(reference);
										setOpen(false);
									},
								},
							]}
							footer={
								tokenId ? (
									<Button
										type="text"
										onClick={() => {
											onChange(tokenValue ?? defaultLiteral(property, design));
											setOpen(false);
											setEditing(true);
										}}
									>
										{t('designTokens.unlink')}
									</Button>
								) : undefined
							}
						/>
					) : editing ? (
						<LiteralEditor
							property={property}
							value={tokenValue ?? effective ?? defaultLiteral(property, design)}
							fonts={fonts}
							disabled={disabled}
							onSave={next => {
								onChange(next);
								setEditing(false);
							}}
							onClose={() => setEditing(false)}
						/>
					) : null
				}
			>
				<Tooltip title={hint} open={open || editing ? false : undefined}>
					<button
						type="button"
						className={tokenId ? css.tokenButton : css.literalButton}
						disabled={disabled}
						aria-label={`${assignmentLabel ?? t(`presets.properties.${property}`)}: ${label}${inheritedHint ? ` (${inheritedHint})` : ''}`}
						data-unresolved={unresolved}
						onClick={() => (tokenId ? openPicker() : setEditing(true))}
					>
						{isColor && exactValue !== undefined ? (
							<span className={css.colorSwatch} style={{ backgroundColor: String(exactValue) }} />
						) : null}
						{token ? (
							<span className={css.bindingLabel}>
								<span className={css.bindingValue}>{String(displayedValue)}</span>
								<span className={css.bindingName}>{token.name}</span>
							</span>
						) : (
							label
						)}
						{unresolved ? <WarningOutlined /> : null}
					</button>
				</Tooltip>
			</Popover>
			{supportsTokens && !tokenId ? (
				<Tooltip title={t('designTokens.connect')}>
					<button
						type="button"
						className={css.connectToken}
						disabled={disabled}
						aria-label={t('designTokens.connect')}
						onClick={openPicker}
					>
						<PlusOutlined />
					</button>
				</Tooltip>
			) : null}
			{assigned !== undefined ? (
				<Tooltip title={t(isBase ? 'presets.clearAssignment' : 'presets.revert')}>
					<button
						type="button"
						className={css.revert}
						disabled={disabled}
						aria-label={t(isBase ? 'presets.clearAssignment' : 'presets.revert')}
						onClick={() => {
							setOpen(false);
							setEditing(false);
							onChange(undefined);
						}}
					>
						<UndoOutlined />
					</button>
				</Tooltip>
			) : null}
		</div>
	);
}

export function PlatformAssignment({
	property,
	preset,
	platform,
	design,
	mediaQuery,
	modeSelection,
	fonts,
	disabled,
	assignmentLabel,
	onChange,
}: {
	property: DesignPresetProperty;
	preset: DesignPreset;
	platform: 'base' | StoryDefaultPlatform;
	design: DesignDefinition;
	mediaQuery: StoryMediaQuery;
	modeSelection: Record<string, string>;
	fonts: { fontFamily: string }[];
	disabled?: boolean;
	assignmentLabel?: string;
	onChange: (next: DesignPresetValue | undefined) => void;
}) {
	const assignment = preset.properties[property] ?? {};
	const inherited =
		platform === 'base' ? undefined : getDesignPresetInheritedAssignment(assignment, platform, mediaQuery);
	return (
		<AssignmentCell
			property={property}
			design={design}
			fonts={fonts}
			modeSelection={modeSelection}
			disabled={disabled}
			isBase={platform === 'base'}
			assignmentLabel={assignmentLabel}
			assigned={platform === 'base' ? assignment.base : assignment.platforms?.[platform]}
			inherited={
				inherited
					? {
							...inherited,
							source:
								inherited.source === 'Base'
									? 'Base'
									: getDesignPresetPlatformName(
											inherited.source,
											mediaQuery.config[inherited.source]?.uiName
										),
						}
					: undefined
			}
			display={getDesignPresetDisplayValue({ property, assignment, platform, mediaQuery, design, modeSelection })}
			onChange={onChange}
		/>
	);
}
